import groupings from './groupings';

export default {
  data() {
    return {
      memoFlashcardsBySection: {},
    };
  },
  mixins: [groupings],
  computed: {
    simCategoryState() {
      return this.$store.getters['categoryStates/categoryStates'][this.sortingSimId];
    },
    randomQuestionOrder() {
      return this.sortingSimConfig?.randomQuestionOrder || false;
    },
    salt() {
      return this.randomQuestionOrder ? this.simCategoryState?.id || 1 : 1;
    },
    sortingSimId() {
      return this.$route.params.simId || this.$route.params.categoryId;
    },
    sortingSimConfig() {
      return this.$store.getters['simulationConfigs/all'].find(c => c.categoryId === this.sortingSimId);
    },
  },
  watch: {
    salt: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.resetMemoFlashcardsBySection();
        }
      },
      immediate: true,
    },
  },
  methods: {
    flashcardsBySection(categoryId) {
      if (!categoryId) return [];

      if (this.memoFlashcardsBySection[categoryId]) {
        return this.memoFlashcardsBySection[categoryId];
      }

      const flashcardList = this.$store.getters['flashcards/flashcardList'].filter(f => f);
      // Don't cache empty results - flashcards may not be loaded yet
      if (flashcardList.length === 0) return [];
      const category = this.$store.getters['categories/categories'][categoryId];

      // If this category has children (sections), gather flashcards from all sections
      if (category && category.children && category.children.length > 0) {
        const allCategories = this.$store.getters['categories/all'];
        const sections = allCategories
          .filter(c => category.children.some(childId => String(childId) === String(c.id)))
          .sort((a, b) => a.number - b.number);

        // Gather flashcards from each section in order
        const flashcardsBySection = sections.map(section => {
          return flashcardList
            .filter(f => f.categoryIds.some(id => String(id) === String(section.id)))
            .sort(this.flashcardSortComparator);
        });

        // Flatten the array
        const flattened = flashcardsBySection.flat();
        this.memoFlashcardsBySection[categoryId] = flattened;
        return flattened;
      }

      // Otherwise, just filter by the single categoryId
      const filtered = flashcardList
        .filter(f => f.categoryIds.some(id => String(id) === String(categoryId)))
        .sort(this.flashcardSortComparator);

      this.memoFlashcardsBySection[categoryId] = filtered;
      return filtered;
    },
    flashcardStateComparator(stateA, stateB) {
      const flashcards = this.$store.getters['flashcards/flashcards'];
      const flashcardA = flashcards[stateA.flashcardId];
      const flashcardB = flashcards[stateB.flashcardId];
      return this.flashcardSortComparator(flashcardA, flashcardB);
    },
    flashcardSortComparator(flashcardA, flashcardB) {
      if (!flashcardA || !flashcardB) return 0;
      const groupAId = this.getScenarioBoxGroupingId(flashcardA.id);
      const groupBId = this.getScenarioBoxGroupingId(flashcardB.id);

      // if the flashcard is not in a group, we want to use the flashcard id
      // as the pseudo-random so that they are interleaved between flashcards with groups
      // otherwise they will all clump together
      const groupAOrder = this.pseudoRand(groupAId || flashcardA.id, this.salt);
      const groupBOrder = this.pseudoRand(groupBId || flashcardB.id, this.salt);

      // if the flashcards are in the same group, we want to sort them by their
      // order in the group
      let flashcardAOrder = 0, flashcardBOrder = 0;

      if (groupAId) {
        const groupA = this.$store.getters['groupings/all'][groupAId];
        flashcardAOrder = groupA.assetAssociations.find(aa => aa.assetBId === flashcardA.id)?.order || 0;
      }

      if (groupBId) {
        const groupB = this.$store.getters['groupings/all'][groupBId];
        flashcardBOrder = groupB.assetAssociations.find(aa => aa.assetBId === flashcardB.id)?.order || 0;
      }

      if (groupAId && groupBId && groupAId === groupBId) {
        return flashcardAOrder - flashcardBOrder;
      }
      return groupAOrder - groupBOrder;
    },
    /**
     * Generate a pseudo-random number between 0 and 1 based on the given number and salt.
     * @param {number} num - The number to generate a pseudo-random number for.
     * @param {number} salt - The salt to use for the pseudo-random number.
     * @returns {number} A pseudo-random number between 0 and 1.
     *
     * This should give a deterministic result for the same input and salt. And since the salt
     * is the category state id, it will give a different result for each category state, meaning
     * different for each user and different for each time they reset the simulation.
     */
    pseudoRand(num, salt = 1) {
      const x = Math.sin(num + salt) * 10000;
      return x - Math.floor(x);
    },
    resetMemoFlashcardsBySection() {
      this.memoFlashcardsBySection = {};
    },
  },
};
